import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_ADMIN_TABLE_PREFS, setAdminTablePrefs } from '@/features/table/adminTablePrefsSlice';
import { toListRequestBody } from '../api/listParams';

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

/**
 * Search, filters, page size and sort for an admin list. Filters, page size and sort are
 * kept in the user's saved admin table prefs (adminTablePrefs[table], in localStorage) like
 * the CRM table prefs, so they're remembered between visits. The search is kept in the URL
 * (?search=) and isn't saved. The current page is local, and goes back to the first page
 * when the search, a filter or the sort changes.
 *
 * Lists searched in the browser use gridProps, search and filters. Lists paged on the
 * server also send listParams as their list request body.
 *
 * @param {string} table the admin table's key in adminTablePrefsSlice, e.g. 'featureFlags'
 * @returns {{
 *   gridProps: object, spread onto AdminDataGrid
 *   search: string
 *   setSearch: (search: string) => void
 *   filters: object, { [name]: value }
 *   setFilter: (name: string, value: *) => void, undefined removes the filter
 *   clearFilters: () => void, removes every filter (the search stays)
 *   listParams: object, the list request body, for lists paged on the server
 * }}
 */
export const useAdminListParams = (table) => {
	const dispatch = useDispatch();
	const prefs = useSelector((state) => state.adminTablePrefs[table] ?? DEFAULT_ADMIN_TABLE_PREFS);
	const [searchParams, setSearchParams] = useSearchParams();
	const [page, setPage] = useState(0);

	const search = searchParams.get('search') ?? '';
	const { filters, sort: sortModel, pageSize } = prefs;
	const paginationModel = useMemo(() => ({ page, pageSize }), [page, pageSize]);

	const savePrefs = (changes) => dispatch(setAdminTablePrefs(table, { ...prefs, ...changes }));

	const setSearch = (value) => {
		setSearchParams(
			(previous) => {
				const next = new URLSearchParams(previous);
				if (value) {
					next.set('search', value);
				} else {
					next.delete('search');
				}
				return next;
			},
			{ replace: true },
		);
		setPage(0);
	};

	const setFilter = (name, value) => {
		const otherFilters = Object.fromEntries(
			Object.entries(filters).filter(([filterName]) => filterName !== name),
		);
		savePrefs({
			filters: value === undefined ? otherFilters : { ...otherFilters, [name]: value },
		});
		setPage(0);
	};

	const clearFilters = () => {
		savePrefs({ filters: {} });
		setPage(0);
	};

	const listParams = useMemo(
		() => toListRequestBody({ paginationModel, sortModel, filters, search }),
		[paginationModel, sortModel, filters, search],
	);

	return {
		gridProps: {
			paginationModel,
			onPaginationModelChange: (model) => {
				setPage(model.page);
				if (model.pageSize !== pageSize) {
					savePrefs({ pageSize: model.pageSize });
				}
			},
			sortModel,
			onSortModelChange: (model) => {
				savePrefs({ sort: model });
				setPage(0);
			},
		},
		search,
		setSearch,
		filters,
		setFilter,
		clearFilters,
		listParams,
	};
};
